import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { secureStorage } from "../utils/secureStorage";
import { axiosPublic , logoutRequest } from "../axios";

export const useAuth = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // =====================
    // Helper pour récupérer l'ID utilisateur
    // =====================
    const getUserId = () => secureStorage.get('userId');

    // =====================
    // Vérification d'authentification
    // =====================
    const checkAuth = async () => {
        try {
            const userId = getUserId();
            const token = secureStorage.get('access_token');

            if (!userId || !token) {
                setUser(null);
                setLoading(false);
                return false;
            }

            // Récupérer les infos utilisateur depuis l'API
            const res = await axiosPublic.get('/api/user', {
                headers: { Authorization: `Bearer ${token}` }
            });

            const userData = res.data.data;
            setUser(userData);
            setLoading(false);
            return true;

        } catch (err) {
            console.error('[checkAuth]', err);
            setUser(null);
            setLoading(false);
            return false;
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    // =====================
    // Login
    // =====================
    const login = async ({ token, refresh_token, userData }) => {
        try {
            // Stockage sécurisé
            secureStorage.set('access_token', token);
            secureStorage.set('refresh_token', refresh_token);
            secureStorage.set('userUid', userData.uid);
            secureStorage.set('userId', userData.id);
            secureStorage.set('isAdmin', userData.is_admin);
            secureStorage.set('isPartner', userData.is_partner);

            // Stockage local
            localStorage.setItem('username', userData.username);
            localStorage.setItem('name', userData.name);
            localStorage.setItem('email', userData.email);
            localStorage.setItem('avatar', userData.avatar || '');
            localStorage.setItem('userType', userData.type);
            localStorage.setItem('code', userData.code);
            localStorage.setItem('user', JSON.stringify({
                name: userData.name,
                username: userData.username,
                email: userData.email,
                avatar: userData.avatar,
                type: userData.type,
                uid: userData.uid,
                id: userData.id,
                is_admin: userData.is_admin,
                is_partner: userData.is_partner
            }));

            setUser(userData);

        } catch (err) {
            console.error('[login]', err);
        }
    };

    // =====================
    // Logout
    // =====================
    const logout = () => {
        const token = secureStorage.get('access_token');

        // UX immédiate
        secureStorage.clear();
        localStorage.clear();
        setUser(null);
        navigate('/');

        // Backend async (fire-and-forget sécurisé)
        logoutRequest(token).catch(() => {});
    };


    // =====================
    // Getters sécurisés
    // =====================
    const getAccessToken = () => secureStorage.get('access_token');
    const getRefreshToken = () => secureStorage.get('refresh_token');
    const getUserUid = () => secureStorage.get('userUid');
    const getIsAdmin = () => secureStorage.get('isAdmin') === 'true';
    const getIsPartner = () => secureStorage.get('isPartner') === 'true';

    const getUsername = () => localStorage.getItem('username');
    const getName = () => localStorage.getItem('name');
    const getEmail = () => localStorage.getItem('email');
    const getAvatar = () => localStorage.getItem('avatar');
    const getUserType = () => localStorage.getItem('userType');
    const getCode = () => localStorage.getItem('code');
    const getUser = () => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return null;

        const userData = JSON.parse(storedUser);

        return {
            id: userData.id,
            uid: userData.uid,
            username: userData.username,
            name: userData.name,
            email: userData.email,
            avatar: userData.avatar || '',
            type: userData.type,
            code: userData.code,
            isAdmin: userData.is_admin,
            isPartner: userData.is_partner,
            isLoggedIn: !!userData.id,
            accessToken: secureStorage.get('access_token'),
            refreshToken: secureStorage.get('refresh_token'),
        };
    };

    // =====================
    // Utilitaires pour API
    // =====================
    const apiGet = async (url) => {
        const token = getAccessToken();
        const userId = getUserId();
        if (!token || !userId) throw new Error("Utilisateur non connecté");

        const res = await axiosPublic.get(url, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return res.data;
    };

    // =====================
    // Statut utilisateur
    // =====================
    const isLoggedIn = () => !!user;       // fonction principale
    const is_loggedIn = () => !!user;      // alias pour compatibilité avec anciens composants
   // console.log(isLoggedIn());
    return {
        user,
        loading,
        isLoggedIn,
        is_loggedIn,
        login,
        logout,
        checkAuth,
        getAccessToken,
        getRefreshToken,
        getUserUid,
        getUserId,
        getIsAdmin,
        getIsPartner,
        getUsername,
        getName,
        getEmail,
        getAvatar,
        getUserType,
        getCode,
        getUser,
        apiGet
    };
};
